import React from "react";
import { ViewportType } from "../../../../shared/constants";
import { validateProps } from "../../../../shared/props";
import { makeStyles, mergeClasses } from "../../../../styles/makeStyles";
import { media } from "../../../../styles/media";
import { ComparisonFeatureCssProps } from "../ComparisonFeature";

export interface ComparisonFeatureGroupProps {
    /** The comparison cards to render in this group */
    readonly children?: React.ReactNode;
    readonly variants?: {
        /**
         * Which viewPort the group should start laying out some children
         * horizontally.
         */
        readonly horizontalLayoutBreakpoint: Extract<ViewportType, "l" | "xl">;
    };
}

const useDefaultStyles = makeStyles({
    // Using mobile-first styling here. Default styles are for single column
    // mobile which all variations share. Variations change how the content
    // expands to fill up the extra space on larger screens
    root: {
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        listStyle: "none",
        padding: 0,
        margin: 0,
        [ComparisonFeatureCssProps.columns]: "auto",
        "> *": {
            flex: 1,
        },
    },
    groupItem: {
        display: "flex",
        "> *": {
            flex: 1,
        },
    },
});

const wrapChildrenRules = {
    flexDirection: "row",
    flexWrap: "wrap",
    "> *": {
        // The goal is that each child takes up half the available space and so
        // two children are on each "row". However, using 50% here did not put
        // two children on each "row". 49% did. I assume this is because flexbox
        // layout doesn't accommodate the specified gap. So 50% + 50% + gap >
        // 100% so all the children end up in a single column. I could try doing
        // something like calc(50% - gap / 2) but I'm not sure if that'll work
        // and this suffices for our purposes
        flexBasis: "49%",
    },
};

const singleRowChildrenRules = {
    "> *": {
        flexBasis: "0",
    },
};

const firstChildFullWidthRules = {
    "> :first-child": {
        flexBasis: "100%",
        [ComparisonFeatureCssProps.columns]: 2,
    },
};

const inlineFirstChildRules = {
    "> :first-child": {
        flexBasis: "0",
        [ComparisonFeatureCssProps.columns]: "auto",
    },
};

const useThreeChildrenStyles = makeStyles({
    rootL: {
        [media.minL]: {
            flexDirection: "row",
        },
    },
    rootXl: {
        [media.minXl]: {
            flexDirection: "row",
        },
    },
});

const useEvenChildrenStyles = makeStyles({
    rootL: {
        [media.minL]: {
            ...wrapChildrenRules,
        },
        [media.minXl]: {
            ...singleRowChildrenRules,
        },
    },
    rootXl: {
        [media.minXl]: {
            ...wrapChildrenRules,
        },
        [media.minXxl]: {
            ...singleRowChildrenRules,
        },
    },
});

const useOddChildrenStyles = makeStyles({
    rootL: {
        [media.minL]: {
            ...wrapChildrenRules,
            ...firstChildFullWidthRules,
        },
        [media.minXl]: {
            ...singleRowChildrenRules,
            ...inlineFirstChildRules,
        },
    },
    rootXl: {
        [media.minXl]: {
            ...wrapChildrenRules,
            ...firstChildFullWidthRules,
        },
        [media.minXxl]: {
            ...singleRowChildrenRules,
            ...inlineFirstChildRules,
        },
    },
});

export const ComparisonFeatureGroup: React.FC<ComparisonFeatureGroupProps> = ({
    children,
    variants,
    ...otherProps
}) => {
    // Validations
    validateProps("ComparisonFeatureGroup", otherProps);

    const defaultClasses = useDefaultStyles();
    const threeChildClasses = useThreeChildrenStyles();
    const evenChildClasses = useEvenChildrenStyles();
    const oddChildClasses = useOddChildrenStyles();

    const rootClassKey: keyof typeof threeChildClasses =
        variants?.horizontalLayoutBreakpoint === "l" ? "rootL" : "rootXl";
    const childrenCount = React.Children.count(children);
    const isEvenChildCount = childrenCount % 2 === 0; // tslint:disable-line no-magic-numbers

    const rootClass = mergeClasses(
        defaultClasses.root,
        childrenCount <= 3 && threeChildClasses[rootClassKey], // tslint:disable-line no-magic-numbers
        childrenCount > 3 && isEvenChildCount && evenChildClasses[rootClassKey], // tslint:disable-line no-magic-numbers
        childrenCount > 3 && !isEvenChildCount && oddChildClasses[rootClassKey] // tslint:disable-line no-magic-numbers
    );

    return (
        <ul className={rootClass} {...otherProps}>
            {React.Children.map(children, (child, i) => (
                <li className={defaultClasses.groupItem} key={i}>
                    {child}
                </li>
            ))}
        </ul>
    );
};
